// @flow
import { combineActions, handleActions } from 'redux-actions';

import { addInstitutionLoginAndAccountsResponse, deleteInstitutionLoginSuccess } from 'data/institutionLogins/actions';
import { mkQcsSyncResourcesPayload, mkQcsSyncResourcesSyncActions } from 'data/qcsSyncResourceTypes';
import { upsertResource, upsertResources, resetResources, completeWithError } from 'data/qcsSyncResourceReducerHelpers';
import { deleteGoalSuccess, deleteGoal, deleteGoalFailure } from 'data/goals/actions';
import { getTransactions } from 'data/transactions/actions';
import { persistDataLoadComplete } from 'data/actions';

import * as actions from './actions';
import { makeAccountStore } from './types';

// SYNC_SIDE_EFFECTS
const addSyncActions = (payload, dispatcher) =>
  payload.set('syncActions',
    mkQcsSyncResourcesSyncActions({ syncDeleteAction: actions.syncDeletedAccount })).set('asyncDispatch', dispatcher);

const reducer = handleActions({

  [persistDataLoadComplete]: (state) => state.merge({
    isLoading: false,
    isLoadingInvestments: false,
    isRefreshing: false,
  }),

  // Called when triggering a CRUD operation on transactions. Sagas are also
  // expected to handle the action by initiating the CRUD operation with QCS.
  //
  [combineActions(
    actions.getAccounts,
    actions.createAccount,
    actions.updateAccount,
    actions.deleteAccount,
    actions.createInvestmentAccount,
    actions.updateInvestmentAccount,
    actions.deleteInvestmentAccount
  )]: (state) => state.set('isLoading', true),

  // TODO need to resolve shared store with different sagas and loading/pending flags
  // for get investment accounts, we don't utilize shared isLoading and loadPending flags
  // this will possibly cause issues for datasets with ONLY investment accounts
  [combineActions(
    actions.getInvestmentAccounts,
  )]: (state) => state,

  // Called when a CRUD operation is successful on transactions. The success
  // action is triggered by the Sagas.
  [combineActions(
    actions.getAccountsSuccess,
  )]: (state, { asyncDispatch, payload }) =>
    upsertResources(state, addSyncActions(payload, asyncDispatch)).set('loadPending', false), // SYNC_SIDE_EFFECTS


  // TODO see above
  // we preserve the current setting of loadPending and isLoading, they are only driven by getAccounts
  [combineActions(
    actions.getInvestmentAccountsSuccess
  )]: (state, { payload }) => upsertResources(state, payload).set('loadPending', state.get('loadPending')).set('isLoading', state.get('isLoading')),

  [combineActions(
    deleteGoalSuccess,
    deleteGoal
  )]:(state, { asyncDispatch, payload: goal }) => {
    let newAcct = state.resourcesById.get(goal?.accountId);
    if (newAcct) {
      newAcct = newAcct.set('isDeleted', true);
      // return upsertResource(state, newAcct);
      // have to dispatch this so the transactions reducer can process it
      asyncDispatch(actions.deleteAccountSuccess(newAcct));  // processing this in account reducer deletes from REDUX
    }
    return state;
  },

  [deleteInstitutionLoginSuccess]:(state, { asyncDispatch, payload: resource }) => {

    // find and delete all accounts (in REDUX only) that have this institutionLoginId
    let acctsToDelete = state.resourcesById.filter((acct) => acct.institutionLoginId === resource.id);
    if (resource.id && acctsToDelete && acctsToDelete.size > 0) {
      acctsToDelete = acctsToDelete.map((x) => x.set('isDeleted', true));
      acctsToDelete.forEach((acct) => asyncDispatch(actions.deleteAccountSuccess(acct)));

      // asyncDispatch(actions.upsertAccountsSuccess(acctsToDelete));
      // give QCS 3seconds to delete the transactions, then do a get
      // note that transactions reducer will not process this as a delete since it went
      // through UPSERT for bulk delete
      // TODO: Listen to upsertAccountsSuccess in transactions reducer
      // This is to get impacted transfers from other accts, but does not return actual deleted transactions from account
      // todo: BUMP SERVER
      setTimeout(() => asyncDispatch(getTransactions()), 3000);
    }
    return state;
  },

  [combineActions(
    actions.upsertAccountsSuccess,
    actions.upsertInvestmentAccountsSuccess,
  )]: (state, { payload: resources }) => {
    // when ignoring accounts during an Add-FI we had to send to QCS but we not want to add to redux
    const resourcesExcludingIgnored = resources.filter((resource) => !resource.isIgnored);
    const payload = mkQcsSyncResourcesPayload({ resources: resourcesExcludingIgnored, doMerge: true });
    return upsertResources(state, payload).set('loadPending', false);
  },

  // Called when a CRUD operation is successful on transactions. The success
  // action is triggered by the Sagas.
  [combineActions(
    actions.createAccountSuccess,
    actions.updateAccountSuccess,
    actions.deleteAccountSuccess,
    actions.createInvestmentAccountSuccess,
    actions.updateInvestmentAccountSuccess,
    actions.deleteInvestmentAccountSuccess
  )]: (state, { payload: accountToUpsert }) => upsertResource(state, accountToUpsert).set('isLoading', false),

  // Called when a CRUD operation fails on transactions. The failure action
  // is triggered by Sagas.
  [combineActions(
    actions.getInvestmentAccountsFailure,
  )]: (state, { payload: _error }) => state,

  [combineActions(
    actions.getAccountsFailure,
    actions.getInvestmentAccountsFailure,
  )]:
    (state, { payload: error }) => completeWithError(state, error),

  [combineActions(
    actions.createAccountFailure,
    actions.updateAccountFailure,
    actions.deleteAccountFailure,
    actions.createInvestmentAccountFailure,
    actions.updateInvestmentAccountFailure,
    actions.deleteInvestmentAccountFailure,
    deleteGoalFailure,
  )]: (state, { payload: error, asyncDispatch }) => {
    asyncDispatch(actions.getAccounts());
    return resetResources(state, error);
  },

  [addInstitutionLoginAndAccountsResponse]: {
    next(state, { payload: { accounts }}) {
      return upsertResources(state, mkQcsSyncResourcesPayload( { resources: accounts } ));
    },
  },

}, makeAccountStore());

export const REDUCER_KEY = 'accountsStore';
export default reducer;
